<template>
    <div>
        <div v-if="referenceSubmitted === true" class="w-full md:w-4/5 m-auto py-6 px-12">
            <div class="bg-white bg-gray-100 border px-2 py-2 mt-1 pb-6">
                <div class="px-4 bg-gray-100">
                    <div class="p-8 -mb-4">
                        <h1 class="mb-8 font-bold text-3xl">Referee Portal</h1>
                        <p>
                            Thank you for submitting this reference. If you have any questions, please contact the LSHTM Admissions team.
                        </p>
                        <hr class="mt-4 mb-4" />
                        <p><span class="font-bold">Email: </span><a href="mailto:admissions@lshtm.ac.uk">admissions@lshtm.ac.uk</a></p>
                        <p><span class="font-bold">Phone: </span><a href="tel:+442076368638">+44 (0)20 7636 8636</a></p>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="w-full md:w-4/5 m-auto py-6 px-12">
            <div v-if="template!==null" class="bg-white bg-gray-100 border px-2 py-2 mt-1 pb-6">
                <div class="px-4 bg-gray-100">
                    <!-- This referee has locked in their reference. -->
                    <div v-if="locked===1">
                        <div class="p-8 -mb-4">
                            <h1 class="mb-8 font-bold text-3xl">Referee Portal</h1>
                            <p>
                                We have already received a reference from this URL. If you feel as though an error has been made or you would like to make changes
                                to
                                your reference, please contact the school admissions team.
                            </p>
                            <hr class="mt-4 mb-4" />
                            <p><span class="font-bold">Email: </span><a href="mailto:admissions@lshtm.ac.uk">admissions@lshtm.ac.uk</a></p>
                            <p><span class="font-bold">Phone: </span><a href="tel:+442076368638">+44 (0)20 7636 8636</a></p>
                        </div>
                    </div>

                    <div v-if="template==='404'">
                        <div class="p-8 -mb-4">
                            <h1 class="mb-8 font-bold text-3xl">404 - Page not found</h1>
                            <p>The page you were looking for does not exist. You may have mistyped the address, or the page may have moved.</p>
                        </div>
                    </div>


                    <div v-if="template==='index' && locked!==1">
                        <div class="p-8 -mb-8">
                            <h1 class="mb-8 font-bold text-3xl">Referee Portal</h1>
                            <p class="text-left mb-3">
                                {{ userData.first_name }} {{ userData.last_name }} is in the process of applying to study {{ userData['programme'] }} at
                                London School of Hygiene & Tropical Medicine and has nominated you as one of their referees. So that their application can be 
                                considered, we would be grateful if you could please submit your reference using the form below at your earliest convenience.
                                You may also upload an existing letter of reference in PDF format. All references must be completed in English.
                            </p>
                            <p class="text-left">
                                When completing your reference, please detail the skills, qualities, experience, motivation, suitability and potential of this 
                                applicant. Please be aware that our selection process considers applicants against the following criteria:
                            </p>
                            <p class="text-left">
                                (a) the applicant's potential to succeed in their chosen programme of study;
                            </p>
                            <p class="text-left">
                                (b) actual or expected academic or professional qualifications and grades;
                            </p>
                            <p class="text-left">
                                (c) relevant work experience;
                            </p>
                            <p class="text-left mb-3">
                                (d) the applicant's personal statement of interest in their chosen programme of study.
                            </p>
                            <p class="text-left mb-3">
                                Entry requirements and information about our programmes is available on our website at <a target="_blank" href="https://www.lshtm.ac.uk/study/courses">Courses|LSHTM</a>.
                            </p>
                            <p class="text-left mb-3">
                                This form will expire on {{ userData['expirationDate'] }} at {{ userData['expirationTime'] }}. If you need to access it after this date, or 
                                have any questions, please email <a href="mailto:admissions@lshtm.ac.uk">admissions@lshtm.ac.uk</a>.
                            </p>
                        </div>

                        <div id="personal_details" class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                             @click="personalDetailsShow = !personalDetailsShow; referenceDetailsShow=false; documentationShow=false;"
                        >
                            <h1 class="text-lg leading-10">Personal Details</h1>
                            <icon :name="!personalDetailsShow ? 'cheveron-right' : 'cheveron-down'"
                                  class="fill-current w-7 h-10 ml-2"
                            />
                        </div>
                        <div id="personal_details_content" :class="{ hide: !personalDetailsShow }"
                             class="bg-white border border-t-0 border-gray-300 overflow-hidden"
                        >
                            <div>
                                <referee-form :referee-data="refereeData"
                                              @formDirty="refereeFormIsDirty"
                                />
                            </div>
                        </div>

                        <!-- Reference Fields -->
                        <div id="referenceDetails" class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                             @click="referenceDetailsShow = !referenceDetailsShow; personalDetailsShow=false; documentationShow=false;"
                        >
                            <h1 class="text-lg leading-10">Reference Details</h1>
                            <icon :name="!referenceDetailsShow ? 'cheveron-right' : 'cheveron-down'"
                                  class="fill-current w-7 h-10 ml-2"
                            />
                        </div>
                        <div id="reference_details" :class="{ hide: !referenceDetailsShow }"
                             class="bg-white border border-t-0 border-gray-300 overflow-hidden"
                        >
                            <div>
                                <reference-form
                                    :reference-data="referenceData"
                                    :user-data="userData" @referenceSaved="referenceSaved"
                                    @formDirty="referenceFormIsDirty"
                                />
                            </div>
                        </div>
                        <div id="supporting_documents" class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                             @click="documentationShow = !documentationShow; personalDetailsShow=false; referenceDetailsShow=false;"
                        >
                            <h1 class="text-lg leading-10">Provide Supporting Documents</h1>
                            <icon :name="!documentationShow ? 'cheveron-right' : 'cheveron-down'"
                                  class="fill-current w-7 h-10 ml-2"
                            />
                        </div>
                        <div id="supporting_documents_content" :class="{ hide: !documentationShow }"
                             class="bg-white border border-t-0 border-gray-300 overflow-hidden"
                        >
                            <div>
                                <div class="p-8" @change="dirtyForm()">
                                    <documentation :documentation="documents"
                                                   :document-types="documentTypes"
                                                   :is-reference-document="true"
                                                   :application-ref="applicationId"
                                                   :reference-doc-type="referenceDocType"
                                                   :reference-form-dirty="referenceFormDirty"
                                                   :referee-form-dirty="refereeFormDirty"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="lg:flex justify-end w-full pb-2 -mt-4">
                            <div class="relative w-auto lg:w-1/3 mx-8">

                                <application-component-save-button :disabled="!formDirty && (referenceData['created_at'] !== referenceData['updated_at'])"
                                                                   class="w-full flex btn-green ml-auto mt-8 md:inline-block text-center"
                                                                   type="button" @click="updateAndLock()"
                                >
                                    Submit reference to Admissions team
                                    <div v-if="loading" class="lds-ring-container absolute top-10">
                                        <div class="lds-ring">
                                            <div />
                                            <div />
                                            <div />
                                            <div />
                                        </div>
                                    </div>
                                </application-component-save-button>
                                <p v-if="referenceData['created_at'] === referenceData['updated_at']" class="-mt-8 text-black-400">(Reference is incomplete)</p>
                            </div>
                        </div>
                    </div>
                    <div v-if="template==='expired'" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '@/shared/Icon'
import ApplicationComponentSaveButton from '@/shared/ApplicationComponentSaveButton'
import Documentation from '@/views/RefereePortal/Documentation'
import ReferenceForm from '@/views/RefereePortal/ReferenceForm'
import RefereeForm from '@/views/RefereePortal/RefereeForm'

export default {
    name: 'Index',
    components: {
        RefereeForm,
        ApplicationComponentSaveButton,
        Icon,
        Documentation,
        ReferenceForm,
    },
    metaInfo: {title: 'Referee Portal'},

    props: {
        displayData: Object,
    },
    data() {
        return {
            refereeFormDirty: false,
            referenceFormDirty: false,
            referenceSubmitted: false,
            personalDetailsShow: true,
            referenceDetailsShow: false,
            documentationShow: false,
            userData: {},
            refereeData: null,
            referenceData: null,
            locked: false,
            form: {
                title: null,
                name: null,
                position: null,
                email: null,
                reference: null,
                address_line1: null,
                address_line2: null,
                address_line3: null,
                city_town: null,
                org: null,
                post_zip_code: null,
                county_state: null,
                country_id: null,
                phone_number: null,
                type_id: null,
                errors: {},
            },

            formDirty: false,
            loading: false,
            successMessage: '',
            serverErrors: '',
            template: null,
            scoreOptions: [5, 4, 3, 2, 1, 0],
            scoreLabels: ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor', 'Unable to comment'],
            scoreOptionsSmall: [1, 2, 3, 4],
            overallRecommendationOptions: ['Unable to comment', 'Do not recommend this applicant', 'Recommend this applicant', 'Strongly recommend this applicant'],
            academicOptions: ['Unable to comment', 'Below Average', 'Average', 'Above Average', 'Outstanding: top 5%'],
            documentTypes: [],
            documents: [],
            referenceDocType: null,
            applicationId: null,
            countries_of_domicile: null,

        }
    },
    computed: {
        validationErrors: {
            get: function () {
                return this.referenceForm.errors
            },
            set: function (newValue) {
                for (const key in newValue) {
                    this.referenceForm.errors[key] = newValue[key].join(' ')
                }
            },
        },

    },
    watch: {
        getDocumentTypesByPortalToken(data) {
            this.documentTypes = data
        },
    },
    created() {
        this.loadPage()
    },
    methods: {
        referenceFormIsDirty(value){
            this.referenceFormDirty = value
        },
        refereeFormIsDirty(value){
            this.refereeFormDirty = value
        },
        dirtyForm() {
            this.formDirty = true
            this.successMessage = ''
        },
        referenceSaved()
        {
            let today = new Date()
            let date = today.getFullYear()+'-'+(today.getMonth()+1)+'-'+today.getDate()
            let time = today.getHours() + ':' + today.getMinutes() + ':' + today.getSeconds()
            this.referenceData['updated_at'] = date + ' ' + time
        },
        loadPage() {
            this.$store.dispatch('getRefereePortalData', {
                reference: this.$route.params.id,
                token: this.$route.params.token,
            })
                .then((response) => {
                    this.template = response.data.template
                    this.referenceForm = response.data.references.data
                    this.refereeData = response.data
                    this.documentTypes = response.data['docTypes']
                    this.documents = response.data.documents.data
                    this.applicationId = response.data.applicationReference.data.reference
                    this.referenceDocType = response.data.referenceDocType
                    this.locked = response.data['locked_in']
                    this.countries_of_domicile = response.data.countries_of_domicile
                    this.userData = response.data.userData
                    this.referenceData = response.data.referenceData

                    //populate the form
                    if (this.$route.query.section) {
                        let sect = this.$route.query.section
                        switch (sect) {
                            case 'referenceDetailsShow':
                                this.personalDetailsShow = false
                                this.referenceDetailsShow = true
                                break
                            case 'documentationShow':
                                this.personalDetailsShow = false
                                this.documentationShow = true
                                break
                        }
                    }
                })
                .catch(error => {
                    if (error.response.status && error.response.status === 404) {
                        this.template = '404'
                    }
                })
        },
        updateAndLock() {
            if (confirm('Please be sure that you have finished.  Once you submit this reference, you won\'t be able to return to edit it again. Click Cancel if you have further changes to make')) {
                this.loading = true
                    this.$store.dispatch('updateAndLock', {
                        reference: this.$route.params.id,
                        referenceId: this.referenceForm.reference,
                        token: this.$route.params.token,
                    })
                        .then((response) => {
                            this.loading = false
                            this.serverErrors = ''
                            this.successMessage = response.data
                            this.referenceSubmitted = true
                            // setTimeout(() => {
                            //     window.location.replace('https://www.lshtm.ac.uk')
                            // }, 3000)
                        })
                        .catch(error => {
                            this.loading = false
                            this.serverErrors = Object.values(error.response.data.errors)
                            this.successMessage = ''
                        })
                this.formDirty = false
            }
        },
    },
}
</script>

<style scoped>
.hide {
    display: none !important;
}
</style>


